import {
  Badge,
  InlineStack,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderIconTile,
  PageHeaderTitle,
} from '@customerio/pluma-components/react';
import type { Tag } from '@customerio/hydra-ui-api';
import { AttributeTypeLabel } from '@/features/people';
import type { IdentifiersConfig } from '@/features/people';
import { SendEventButton } from '../components/send-event-button';
import { UseInMenu } from '../components/use-in-menu';
import { itemRefFromAddress } from '../utils/item';
import { DATA_INDEX_ITEM_ICONS, DataIndexLocation, SENSITIVE_PRIVACY_LEVEL } from '../utils/types';
import type { DataIndexItemAddress } from '../urls';
import { ItemActions } from './item-actions';
import type { DataIndexItemDetail } from './hooks';

/**
 * The name of the item and the one thing to do with it.
 *
 * An event's only action is sending one; an attribute's are a menu, because
 * describing it, redacting it and deleting it are all things only its own
 * page offers.
 */

/** Where the send-event modal was opened from, as analytics names it. */
const SEND_EVENT_SOURCE = 'Data Index Event Page';

/** The privacy level the backend uses for an attribute someone marked sensitive. */

type ItemHeaderProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
  item: DataIndexItemDetail;
  /** The description the page shows, which is what editing starts from. */
  description: string | null;
  tags: readonly Tag[];
  identifiers: IdentifiersConfig | null | undefined;
  /** Whether the workspace identifies people by more than one attribute. */
  isMultiIdentifier: boolean;
  /** Whether this attribute can be marked sensitive at all. */
  canBeMarkedSensitive: boolean;
  deleteBlockReason: string | undefined;
};

export function ItemHeader({
  workspaceId,
  address,
  item,
  description,
  tags,
  identifiers,
  isMultiIdentifier,
  canBeMarkedSensitive,
  deleteBlockReason,
}: ItemHeaderProps) {
  return (
    <PageHeader>
      <PageHeaderIconTile name={DATA_INDEX_ITEM_ICONS[address.kind]} />
      <PageHeaderTitle>
        <InlineStack alignBlock="center" gap="100">
          {item.displayName}
          {address.kind === 'attribute' ? (
            <AttributeTypeLabel
              attribute={item.name}
              identifiers={identifiers}
              identifierBadge={{ isMultiIdentifier, workspaceId: String(workspaceId) }}
            />
          ) : null}
          {item.privacyLevel === SENSITIVE_PRIVACY_LEVEL ? <Badge color="blue">Sensitive</Badge> : null}
        </InlineStack>
      </PageHeaderTitle>
      <PageHeaderActions>
        <UseInMenu workspaceId={workspaceId} item={itemRefFromAddress(address)} size="md" />
        {address.kind === 'event' ? (
          <SendEventButton workspaceId={workspaceId} eventName={item.name} source={SEND_EVENT_SOURCE} />
        ) : null}
        {address.kind === 'attribute' ? (
          <ItemActions
            target={{
              workspaceId,
              kind: address.kind,
              location: DataIndexLocation.DetailsPage,
              scope: address.scope,
              name: item.name,
              description: description ?? '',
              tags,
              privacyLevel: item.privacyLevel,
            }}
            canBeMarkedSensitive={canBeMarkedSensitive}
            deleteBlockReason={deleteBlockReason}
          />
        ) : null}
      </PageHeaderActions>
      <PageHeaderDescription>{description ?? 'No description'}</PageHeaderDescription>
    </PageHeader>
  );
}
